import { Grid, makeStyles, Slider} from '@material-ui/core';
import { useContext, useEffect, useState } from 'react';
import { ISelectAllergens, ISelectFoodCategory, ISelectSeating } from '../../interface/interface';
import { planEatApi } from '../../planEatApi/PlanEatApi';
import { IDishContent, ISeatingContent, IVersionedObject } from '../../planEatApi/PlanEatFacade';
import { AllergenButton } from '../ui/AllergenButton';
import { CategoryCheckbox } from '../ui/CategoryCheckbox';
import { FrontendApi } from '../../planEatApi/FrontendApi';
import { prettyDate } from '../../config/dateFormatting';
import { CustomCheckBox } from '../ui/CustomCheckBox';
import { OfficeContext } from '../../config/Context';
import { allergensList } from '../../constants/allergens';
import { foodCategoriesList } from '../../constants/foodCategories';
import { sliderMarks } from '../../constants/sliderValues';
import { CancelCrudButtons } from '../ui/CancelCrudButtons';
import { Modal } from '../ui/Modal';
import { InputContainer } from '../ui/InputContainer';
import { InputField } from '../ui/InputField';
import { clone } from '../../utils/clone';

const useStyles = makeStyles((theme: any) => ({
    inputTitle:{
        fontSize: 18,
        fontWeight: 700,
        paddingBottom: 15
    },
    foodCategoryContainer:{
        display: 'grid',
        gridTemplateColumns: 'auto auto auto',
        gridTemplateRow: '100% 100%',
        columnGap: 15,
        rowGap: 15
    },
    seatingsContainer:{
        background:'#F7F7F8',
        borderRadius: 8,
        height: '100%',
        width: '100%',
        padding: 20
    },
    optionTimesHeader:{
        fontSize: 14,
        fontWeight:700
    },
    selectSeatingContent:{
        display:'flex',
        justifyContent:'space-between',
        alignItems:'center',
        paddingBottom: 10,
        paddingTop: 10,
        borderBottom: '1px dashed rgba(11, 37, 89, 0.2);',
    },
    noSeatingContainer: {
        display:'flex',
        justifyContent:'center',
        alignItems:'center',
        width:'100%',
        height:80,
        color: '#AAAAAA',
    },
    sliderContainer:{
        paddingTop: 40,
        display: 'grid',
        gridTemplateColumns: '100%',
        gridTemplateRow: '100%'
    },
}));

interface Props {
    hidden:boolean;
    setHidden:  React.Dispatch<React.SetStateAction<any>>;
    dish: IDishContent;
    setDish:  React.Dispatch<React.SetStateAction<any>>;
    date: Date;
    postDish: (selectSeating: ISelectSeating[]) => void
    seatings?: IVersionedObject<ISeatingContent>[];
}

export const DishCreationModal: React.FC<Props> = ({hidden,setHidden,dish,setDish,date,postDish,seatings}) => {
    // Styling
    const classes = useStyles();

    // State
    const [allergensView, setAllergensView] = useState<any[]>([]);
    const [selectAllergens, setSelectAllergens] = useState<ISelectAllergens[]>([]);
    const [foodCategoryView, setFoodCategoryView] = useState<any[]>([]);
    const [selectFoodCategories, setSelectFoodCategories] = useState<ISelectFoodCategory[]>([]);
    const [tmpSelectSeatings, setTmpSelectSeatings] = useState<ISelectSeating[]>([]);
    const [selectSeatingsView, setSelectSeatingsView] = useState<any[]>([]);

    // Context
    const officeContext = useContext(OfficeContext);

    useEffect(() => {
        checkIfDish()
    },[hidden, officeContext?.office, dish])

    const checkIfDish = () => {
        if(officeContext?.office){
            if(dish.id !== ''){
                createSelectAllergens(dish);
                createSelectFoodCategories(dish);
                getSelectedSeatings(officeContext?.office.content.id,dish);
            } else{
                createSelectAllergens();
                createSelectFoodCategories();
                getSelectedSeatings(officeContext?.office.content.id);
            }
        }
    }


    const createSelectAllergens = (dish ?: IDishContent) => {
        if(selectAllergens.length === 0 || !selectAllergens.find(allergen => allergen.selected)){
            const tmpSelectAllergens : ISelectAllergens[] = []
            if(dish){
                for(let i=0;i<allergensList.length;i++){
                    var selected = false;
                    dish.allergens.forEach((allergen) =>{
                        if(allergen=== allergensList[i]){
                            selected = true;
                        }
                    })
                    const tmpSelectAllergen : ISelectAllergens ={
                        'allergen' : allergensList[i],
                        'selected' : selected
                    }
                    tmpSelectAllergens.push(tmpSelectAllergen)
                }
            } else{
                for(let i=0;i<allergensList.length;i++){
                    const tmpSelectAllergen : ISelectAllergens ={   
                        'allergen' : allergensList[i],
                        'selected' : false
                    }
                    tmpSelectAllergens.push(tmpSelectAllergen)
                }
            }
            setSelectAllergens(tmpSelectAllergens) 
        }
    }

    const createSelectFoodCategories = (dish ?: IDishContent) => {
        if(selectFoodCategories.length === 0 || !selectFoodCategories.find(foodCategory => foodCategory.selected)){
            const tmpSelectFoodCategories : ISelectFoodCategory[] = []
            if(dish){
                for(let i=0;i<foodCategoriesList.length;i++){
                    var selected = false;
                    dish.foodCategories.forEach((foodCategory) => {
                        if(foodCategory=== foodCategoriesList[i]){
                            selected = true;
                        }
                    })
                    const tmpSelectFoodCategory : ISelectFoodCategory = {
                        'foodCategory' : foodCategoriesList[i],
                        'selected' : selected
                    }
                    tmpSelectFoodCategories.push(tmpSelectFoodCategory)
                }
            } else{
                for(let i=0;i<foodCategoriesList.length;i++){
                    const tmpSelectFoodCategory : ISelectFoodCategory = {
                        'foodCategory' : foodCategoriesList[i],
                        'selected' : false
                    }
                    tmpSelectFoodCategories.push(tmpSelectFoodCategory)
                }
            }
            setSelectFoodCategories(tmpSelectFoodCategories)
        }
    }

    useEffect(() =>{
        if(officeContext?.office){
            if(dish.id !== ""){
                getSelectedSeatings(officeContext?.office.content.id,dish);
            } else {
                getSelectedSeatings(officeContext?.office.content.id);
            }
        }
    },[officeContext?.office, date, dish, seatings])

    const getSelectedSeatings = async (officeId:string, dish?: IDishContent) =>{
        // Get seatings on date
        if(seatings && !tmpSelectSeatings.find(selectedSeating => selectedSeating.selected)){
            FrontendApi.getSeatingsOnDate(date,officeId,seatings).then((seatingsOnDate) => {
                if(dish){
                    FrontendApi.getSeatingOnDish(seatingsOnDate,dish)
                    .then((tmpSelectSeatings) => {
                        setTmpSelectSeatings(tmpSelectSeatings);
                    })
                } else {
                    FrontendApi.getSeatingOnDish(seatingsOnDate)
                    .then((tmpSelectSeatings) => {
                        setTmpSelectSeatings(tmpSelectSeatings);
                    })
                }
            })
        }
    }

    useEffect(() => {
        displaySeatings()
    }, [tmpSelectSeatings]);

    const displaySeatings = () => {
        if(tmpSelectSeatings.length > 0){
            const tmpSelectSeatingsView = tmpSelectSeatings.map((tmpSelectSeating,index) => (
                <div onClick={() => {updateSelectSeating(tmpSelectSeating)}} className={classes.selectSeatingContent}>
                    <div>
                        <b>{prettyDate(tmpSelectSeating.seating.content.seatingStart,true,false)}</b>  {tmpSelectSeating.seating.content.seatingName}
                    </div>
                    <div>
                        <CustomCheckBox selected={tmpSelectSeating.selected}/>
                    </div>
                </div>
            ));
            setSelectSeatingsView(tmpSelectSeatingsView);
        } else {
            const tmpNoSeatingsView : any[] = []
            tmpNoSeatingsView.push(
                <div className={classes.noSeatingContainer}>
                    No seating on date
                </div>
            )
            setSelectSeatingsView(tmpNoSeatingsView);
        }
    }

    useEffect(() => {
        if(selectAllergens.length > 0){
            displayAllergens();
        }
      }, [selectAllergens]);

    useEffect(() => {
        if(selectFoodCategories.length > 0){
            displayFoodCategories();
        }
    }, [selectFoodCategories]);


    const displayAllergens = () => {
        var tmpAllergens: any[] = []
        tmpAllergens = allergensList.map((allergens,index) => (
            <AllergenButton 
                index={index+1}
                selectAllergen={allergens}
                selectAllergens={selectAllergens}
                selected={selectAllergens[index].selected}
                setSelectAllergens={setSelectAllergens}
            /> 
        ));
        setAllergensView(tmpAllergens);
    }

    const displayFoodCategories = () => {
        const tmpFoodCategories = foodCategoriesList.map((foodCategory,index) => (
            <CategoryCheckbox 
                foodCategory={foodCategory}
                selectCategories={selectFoodCategories}
                selected={selectFoodCategories[index].selected}
                setSelectFoodCategories={setSelectFoodCategories}
            />
        ));
        setFoodCategoryView(tmpFoodCategories);
    }

    const updateSelectSeating = (tmpSelectSeating : ISelectSeating) => {
        var tmpSelectSeatingItems : ISelectSeating[] = [];
        tmpSelectSeatings.forEach((selectSeatingItem) =>{
            if(selectSeatingItem.seating.content.id === tmpSelectSeating.seating.content.id){
                selectSeatingItem.selected = !tmpSelectSeating.selected;
            }
            tmpSelectSeatingItems.push(selectSeatingItem)
        })
        setTmpSelectSeatings(tmpSelectSeatingItems);
    }

    const postDishElements = async () => {
        let selectedAllergens : string[] = [];
        for(let i=0;i<selectAllergens.length;i++){
            if(selectAllergens[i].selected){
                selectedAllergens.push(selectAllergens[i].allergen)
            }
        }

        let selectedFoodcategories : string[] = [];
        for(let i=0;i<selectFoodCategories.length;i++){
            if(selectFoodCategories[i].selected){
                selectedFoodcategories.push(selectFoodCategories[i].foodCategory)
            }
        }

        if(dish){
            dish.allergens = selectedAllergens;
            dish.foodCategories = selectedFoodcategories;
        }
        postDish(tmpSelectSeatings)
    };

    const resetCategories = () => {
        var tmpAllergens = [];
        var tmpFoodCategories = [];
        var selectedSeatings = [];

        for(let i=0;i<selectAllergens.length;i++) {
            selectAllergens[i].selected = false;
            tmpAllergens.push(selectAllergens[i]);
        }
        for(let i=0;i<selectFoodCategories.length;i++) {
            selectFoodCategories[i].selected = false;
            tmpFoodCategories.push(selectFoodCategories[i]);
        }

        for(let i=0;i<tmpSelectSeatings.length;i++) {
            tmpSelectSeatings[i].selected = false;
            selectedSeatings.push(tmpSelectSeatings[i]);
        }

        setSelectAllergens(tmpAllergens);
        setSelectFoodCategories(tmpFoodCategories);
        setTmpSelectSeatings(selectedSeatings)
    }

    useEffect(()=>{
        if(hidden){
            resetCategories();
        }
    },[hidden])

    const handleChange = (prop: keyof IDishContent) => (event: React.ChangeEvent<HTMLInputElement>) => {
        setDish({ ...dish, [prop]: event.target.value });
    };

    const handleCo2ImpactChange = (event: any, newValue: number | number[]) => {
        let tmpDish = clone<IDishContent>(dish)
        tmpDish.co2Impact = newValue.toString()
        setDish(tmpDish)
    };
    
    const isCorrectlyFilled = () => {
        if(seatings){
            if(dish.dishTitle.length >= 2 && dish.dishDescription.length > 2){
                let minOneSelected = false;
                tmpSelectSeatings.forEach((seating) => {
                    if(seating.selected){
                        minOneSelected = true;
                    }
                })
                return minOneSelected;
            }
        } else {
            return true
        }
    }

    return (
        <Modal hidden={hidden} setHidden={setHidden} title='New Dish'>
            <InputContainer>
                <InputField
                    label="Dish Title"
                    variant="outlined"
                    multiline
                    rows={4}
                    value={dish.dishTitle}
                    onChange={handleChange('dishTitle')}
                    fullWidth
                />
                <InputField
                    label="Dish Description"
                    variant="outlined"
                    multiline
                    rows={4}
                    value={dish.dishDescription}
                    onChange={handleChange('dishDescription')}
                    fullWidth
                />
                <div>
                    <div className={classes.inputTitle}>Categories</div>
                    <Grid
                        container
                        direction="row"
                        justifyContent="flex-start"
                        alignItems="flex-start"
                        className={classes.foodCategoryContainer}
                    >
                        {foodCategoryView}
                    </Grid>
                </div>
                {   seatings &&
                    <div>
                        <div className={classes.seatingsContainer}>
                            <div className={classes.optionTimesHeader}>
                                Options/times available for this menu?
                            </div>
                            <div>
                                {selectSeatingsView}
                            </div>
                        </div>
                    </div>
                    }
                <div>
                    <div className={classes.inputTitle}>Allergens</div>
                    <Grid
                        container
                        direction="row"
                        justifyContent="flex-start"
                        alignItems="flex-start"
                    >
                        {allergensView}
                    </Grid>
                </div>
            </InputContainer>
            <div className={classes.sliderContainer}>
                <div className={classes.inputTitle}>CO-2 impact</div>
                <Slider
                    aria-label="CO2_slider"
                    step={1}
                    min={1}
                    max={5}
                    marks={sliderMarks}
                    valueLabelDisplay="auto"
                    value={parseInt(dish.co2Impact)}
                    onChange={handleCo2ImpactChange}
                />
            </div>
            <CancelCrudButtons 
                hidden={hidden} 
                setHidden={setHidden} 
                postButtonText={dish.id !== '' ? "Update" : "Create" + " Dish"} 
                isDelete={false} 
                postEntity={postDishElements}  
                disabledPostButton={!isCorrectlyFilled()}/>
        </Modal>
    );
}